import type { FC, ReactNode } from 'react'

import { Quote } from 'lucide-react'
import { Container } from '@/components/generals/container'
import { Reveal } from '@/components/generals/reveal'
import { SectionLabel } from '@/components/generals/section-label'
import { StarRating } from '@/components/generals/star-rating'

import { RATING_BREAKDOWN, REVIEWS } from '@/constants/content'
import { garage } from '@/constants/garage'

type Review = typeof REVIEWS[number]

const ReviewMeta: FC<{ review: Review }> = ({ review }): ReactNode => {
    return (
        <footer className='mt-5'>
            <p className='font-semibold'>
                {review.author}
                <span className='font-normal text-muted-foreground'> · {review.vehicle}</span>
            </p>

            <ul className='mt-3 flex flex-wrap gap-2' aria-label='Intervention et délai'>
                <li className='label-mono rounded-sm border border-border px-2 py-1 text-muted-foreground'>
                    {review.service}
                </li>

                <li className='label-mono rounded-sm border border-foreground/30 px-2 py-1 text-foreground'>
                    {review.timing}
                </li>
            </ul>
        </footer>
    )
}

export const ReviewsSection: FC = (): ReactNode => {
    const [featured, ...others] = REVIEWS

    return (
        <section id='avis' aria-labelledby='avis-title' className='py-20 lg:py-32'>
            <Container className='grid gap-14 lg:grid-cols-12 lg:gap-10 xl:gap-16'>
                <div className='lg:col-span-5'>
                    <SectionLabel index='04'>Avis clients</SectionLabel>

                    <h2 id='avis-title' className='heading mt-5 text-title'>
                        {garage.rating.display} sur 5, et des clients qui reviennent.
                    </h2>

                    <div className='mt-10 flex items-center gap-6 border-y border-border py-6'>
                        <p className='heading text-[4.5rem] leading-none tracking-[-0.04em] tabular-nums'>
                            {garage.rating.display}
                        </p>

                        <div>
                            <StarRating label={`${garage.rating.display} sur 5`} />

                            <p className='mt-2 text-sm text-muted-foreground'>
                                {garage.rating.count} avis vérifiés après intervention
                            </p>
                        </div>
                    </div>

                    <dl className='mt-6 space-y-2.5'>
                        {RATING_BREAKDOWN.map(row => (
                            <div key={row.stars} className='grid grid-cols-[4.5rem_1fr_2.75rem] items-center gap-3 text-sm'>
                                <dt className='text-muted-foreground'>
                                    {row.stars}&nbsp;étoile{row.stars > 1 ? 's' : ''}
                                </dt>

                                <dd className='h-1.5 overflow-hidden rounded-full bg-border' aria-hidden='true'>
                                    <span className='block h-full rounded-full bg-foreground' style={{ width: `${row.share}%` }} />
                                </dd>

                                <dd className='text-right font-mono tabular-nums'>
                                    {row.share}&nbsp;%
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                <div className='lg:col-span-7'>
                    <Reveal>
                        <figure className='relative border-b border-border pb-10'>
                            <Quote aria-hidden='true' className='size-9 text-primary' strokeWidth={1.5} />

                            <blockquote className='mt-5 font-heading text-[clamp(1.375rem,1.1rem+1vw,1.875rem)] font-semibold leading-[1.32] tracking-[-0.015em]'>
                                <p>{featured.text}</p>
                            </blockquote>

                            <figcaption>
                                <StarRating size='sm' label='5 sur 5' className='mt-6' />
                                <ReviewMeta review={featured} />
                            </figcaption>
                        </figure>
                    </Reveal>

                    <ul className='grid sm:grid-cols-2'>
                        {others.map((review, index) => (
                            <li
                                key={review.author}
                                className={index % 2 === 0 ? 'border-b border-border py-8 sm:pr-8' : 'border-b border-border py-8 sm:border-l sm:pl-8'}
                            >
                                <article>
                                    <div className='flex items-center justify-between gap-4'>
                                        <StarRating size='sm' label='5 sur 5' />

                                        <p className='label-mono text-muted-foreground'>
                                            {review.date}
                                        </p>
                                    </div>

                                    <p className='mt-4 leading-relaxed'>
                                        {review.text}
                                    </p>

                                    <ReviewMeta review={review} />
                                </article>
                            </li>
                        ))}
                    </ul>
                </div>
            </Container>
        </section>
    )
}
